import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import ReactDOM from 'react-dom';
import BayColorPicker, {
	type DynamicCopy,
	ADMIN_DYNAMIC_COPY,
} from '@bay-ui/BayColorPicker';
import { PresetColorsContext } from '@extensions/AttributePanel/components/provider/PresetColorsProvider';
import { useEditorProps } from '@bay-email/editor';
import { t } from '@bay-email/core';
import {
	beginFormatInteraction,
	endFormatInteraction,
} from '@extensions/components/Form/RichTextToolBar/components/Tools/formatInteractionGuard';

interface HighlightRect {
	top: number;
	left: number;
	width: number;
	height: number;
}

export interface BrandColor {
	label: string;
	color: string;
	value: string;
}

export interface RteColorPickerProps {
	onChange?: (val: string) => void;
	onCommit?: (val: string) => void;
	value?: string;
	children?: React.ReactNode;
	getPopupContainer?: () => HTMLElement;
	showInput?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	onBeforeOpen?: () => void;
	highlightRange?: Range | null;
	brandColors?: BrandColor[];
	/** Show the BRAND swatch row in static mode. Defaults to true. */
	showBrandColors?: boolean;
	/** Enables the Static / Dynamic toggle for picking a dynamic brand color. */
	supportsDynamic?: boolean;
	/**
	 * Apply a dynamic brand color to the selection: the brand merge tag plus the
	 * chosen fallback hex, kept separate so the caller can drive execCommand and
	 * build the inline merge-tag badge.
	 */
	onDynamicCommit?: (brandValue: string, fallbackHex: string) => void;
	/** Optional copy overrides for the dynamic panel, passed straight to BayColorPicker. */
	dynamicCopy?: DynamicCopy;
}

export function RteColorPicker(props: RteColorPickerProps) {
	const { colors, addCurrentColor } = useContext(PresetColorsContext);
	const { isGlobalRole } = useEditorProps();
	// Global-admin roles get the admin-authoring copy; an explicit prop always wins.
	// The role is threaded in via editor props from the host app's access state —
	// the editor runs against its own redux store (no actingAs slice), so a
	// useSelector here can't see it.
	const resolvedDynamicCopy =
		props.dynamicCopy ?? (isGlobalRole ? ADMIN_DYNAMIC_COPY : undefined);
	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const [highlightRects, setHighlightRects] = useState<HighlightRect[]>([]);
	const pendingRecentColorRef = useRef<string | null>(null);
	const preservedRangeRef = useRef<Range | null>(null);
	const formatGuardHeldRef = useRef(false);

	const { value = '', onChange, children, showInput = !children } = props;

	const presetColors = useMemo(() => {
		const filtered = colors.filter(c => c.toLowerCase() !== 'transparent');
		return [...filtered, 'Transparent'];
	}, [colors]);

	const clearHighlight = useCallback(() => setHighlightRects([]), []);

	const syncPreservedRange = useCallback(() => {
		if (!isPickerOpen && props.highlightRange && !props.highlightRange.collapsed) {
			preservedRangeRef.current = props.highlightRange.cloneRange();
		}
	}, [isPickerOpen, props.highlightRange]);

	const showHighlight = useCallback(() => {
		const range = preservedRangeRef.current;
		if (!range || range.collapsed) { clearHighlight(); return; }
		try {
			const rects = Array.from(range.getClientRects())
				.filter(r => r.width > 0 && r.height > 0)
				.map(r => ({ top: r.top, left: r.left, width: r.width, height: r.height }));
			setHighlightRects(rects);
		} catch { clearHighlight(); }
	}, [clearHighlight]);

	const flushPendingRecentColor = useCallback(() => {
		if (!pendingRecentColorRef.current) return;
		addCurrentColor(pendingRecentColorRef.current);
		pendingRecentColorRef.current = null;
	}, [addCurrentColor]);

	useEffect(() => { syncPreservedRange(); }, [syncPreservedRange]);

	useEffect(() => {
		return () => {
			if (formatGuardHeldRef.current) {
				endFormatInteraction();
				formatGuardHeldRef.current = false;
			}
		};
	}, []);

	useEffect(() => {
		if (!isPickerOpen || highlightRects.length === 0) return;
		const update = () => showHighlight();
		const root = preservedRangeRef.current?.startContainer?.getRootNode?.();
		const hasCustomRoot = Boolean(root && root !== document && 'addEventListener' in (root as EventTarget));
		window.addEventListener('resize', update);
		window.addEventListener('scroll', update, true);
		document.addEventListener('scroll', update, true);
		if (hasCustomRoot) (root as EventTarget).addEventListener('scroll', update, true);
		return () => {
			window.removeEventListener('resize', update);
			window.removeEventListener('scroll', update, true);
			document.removeEventListener('scroll', update, true);
			if (hasCustomRoot) (root as EventTarget).removeEventListener('scroll', update, true);
		};
	}, [highlightRects.length, isPickerOpen, showHighlight]);

	const handleColorChange = useCallback((nextColor: string) => {
		const val = nextColor.toLowerCase() === 'transparent' ? 'Transparent' : nextColor;
		onChange?.(val);
		pendingRecentColorRef.current = val;
	}, [onChange]);

	const handleColorCommit = useCallback((nextColor: string) => {
		const val = nextColor.toLowerCase() === 'transparent' ? 'Transparent' : nextColor;
		addCurrentColor(val);
		pendingRecentColorRef.current = null;
		// Force the just-applied color into the model now. A commit often coincides
		// with the picker closing, after which the editing session may end and rebuild
		// the contenteditable; without a synchronous flush the color would be dropped.
		props.onCommit?.(val);
	}, [addCurrentColor, props]);

	return (
		<>
			<BayColorPicker
				value={value}
				brandColors={props.brandColors}
				showBrandColors={props.showBrandColors ?? true}
				supportsDynamic={Boolean(props.supportsDynamic && props.brandColors?.length)}
				onDynamicCommit={props.onDynamicCommit}
				dynamicCopy={resolvedDynamicCopy}
				onChange={handleColorChange}
				onCommit={handleColorCommit}
				onOpenChange={(isOpen: boolean) => {
					setIsPickerOpen(isOpen);
					props.onOpenChange?.(isOpen);
					if (isOpen) {
						if (!formatGuardHeldRef.current) {
							beginFormatInteraction();
							formatGuardHeldRef.current = true;
						}
						props.onBeforeOpen?.();
						syncPreservedRange();
						showHighlight();
					} else {
						if (formatGuardHeldRef.current) {
							endFormatInteraction();
							formatGuardHeldRef.current = false;
						}
						clearHighlight();
						flushPendingRecentColor();
					}
				}}
				presetColors={presetColors}
				showInput={showInput}
				getPopupContainer={props.getPopupContainer}
				customColorLabel={t('Custom Color...')}
			>
				{children}
			</BayColorPicker>
			{typeof document !== 'undefined' && highlightRects.length > 0 && ReactDOM.createPortal(
				<>
					{highlightRects.map((rect, i) => (
						<div
							key={`${rect.left}-${rect.top}-${i}`}
							style={{
								position: 'fixed',
								top: rect.top,
								left: rect.left,
								width: rect.width,
								height: rect.height,
								backgroundColor: 'rgba(14, 101, 235, 0.22)',
								borderRadius: 2,
								pointerEvents: 'none',
							}}
						/>
					))}
				</>,
				document.body,
			)}
		</>
	);
}
